// Shared member helpers. These lived in duplicate in TeamV2.jsx and
// utils/teamMemberExport.js; the list, the export and the detail panel all need
// the same manager resolution, so it lives once here.

// Members can have a stub email that shadows the real login email in the UI.
export function displayMemberEmail(u) {
  const stub = String(u?.stubEmail || "").trim();
  return stub || u?.email || "";
}

export function getManagerId(manager) {
  if (!manager) return null;
  if (typeof manager === "object") return String(manager._id || manager.id || "") || null;
  return String(manager);
}

// Managers arrive as ids, partial refs or full objects, and there's a legacy
// singular `reportingManager` alongside the plural array.
export function normalizeReportingManagerIds(userOrManagers) {
  const raw = Array.isArray(userOrManagers)
    ? userOrManagers
    : (userOrManagers?.reportingManagers?.length
      ? userOrManagers.reportingManagers
      : (userOrManagers?.reportingManager ? [userOrManagers.reportingManager] : []));
  const ids = raw.map(getManagerId).filter(Boolean);
  return Array.from(new Set(ids));
}

// Turn manager refs into full user objects by looking them up in allUsers.
export function resolveReportingManagers(user, allUsers = []) {
  const mgrs = [];
  const add = (manager) => {
    if (!manager) return;
    if (typeof manager === "object" && (manager._id || manager.id)) {
      if (manager.firstName || manager.email) mgrs.push(manager);
      else {
        const found = allUsers.find((u) => String(u._id || u.id) === String(manager._id || manager.id));
        if (found) mgrs.push(found);
      }
    } else {
      const found = allUsers.find((u) => String(u._id || u.id) === String(manager));
      if (found) mgrs.push(found);
    }
  };
  if (user?.reportingManagers?.length) user.reportingManagers.forEach(add);
  else if (user?.reportingManager) add(user.reportingManager);

  const seen = new Set();
  return mgrs.filter((m) => {
    const id = String(m._id || m.id);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function formatManagerName(m) {
  return `${m.firstName || ""} ${m.lastName || ""}`.trim() || m.email || "Unknown";
}

export function memberName(u) {
  return `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || u?.email || "Member";
}

export function roleLabel(roles, value) {
  return roles.find((r) => r.value === value)?.label || value || "";
}

// Positions an admin may assign. Non-admins are limited to levels strictly below
// their own so they can't promote someone above themselves.
export function getAccessiblePositions(structure, currentUser) {
  const opt = (p) => ({ value: p.roleName, label: p.roleName });
  const all = [];
  (structure || []).forEach((lvl) => (lvl.positions || []).forEach((p) => all.push(opt(p))));
  const role = currentUser?.role;
  if (!structure?.length || role === "main_admin" || role === "org_admin" || !currentUser?.position) return all;

  const norm = (s) => (s || "").trim();
  const myLevel = structure.findIndex(
    (lvl) => (lvl.positions || []).some((p) => norm(p.roleName) === norm(currentUser.position)),
  );
  if (myLevel === -1) return [];
  const lower = [];
  for (let i = myLevel + 1; i < structure.length; i++) (structure[i].positions || []).forEach((p) => lower.push(opt(p)));
  return lower;
}

// Which org-structure level a member sits at, as a 1-based label. Used by the
// permissions panel to explain what a member can see.
export function memberLevel(structure, position) {
  const norm = (s) => (s || "").trim().toLowerCase();
  const i = (structure || []).findIndex(
    (lvl) => (lvl.positions || []).some((p) => norm(p.roleName) === norm(position)),
  );
  return i === -1 ? null : i + 1;
}

export const MEMBER_STATUSES = [
  { value: "active", label: "Active" },
  { value: "all", label: "All statuses" },
  { value: "inactive", label: "Inactive" },
  { value: "archived", label: "Archived" },
  { value: "pending", label: "Pending" },
];

export const AGENT_FILTERS = [
  { value: "", label: "All agents" },
  { value: "yes", label: "Desktop Agent" },
  { value: "no", label: "No agent" },
  { value: "not_active", label: "Not active today" },
];

// Every permission the backend stores, with the labels used in the form and the
// panel. `viewBilling` exists on the schema but has no form toggle yet, so the
// panel is the only place it surfaces.
export const PERMISSION_FIELDS = [
  { key: "addProject", label: "Add projects", desc: "Can create new projects" },
  { key: "addTeamMember", label: "Add team members", desc: "Can invite people" },
  { key: "viewReports", label: "View reports", desc: "Access analytics & reports" },
  { key: "viewActivity", label: "View activity", desc: "See activity timeline & screenshots" },
  { key: "editProfile", label: "Edit profile", desc: "Can edit their own profile" },
  { key: "crmAccess", label: "CRM access", desc: "Can access the CRM module" },
  { key: "viewBilling", label: "View billing", desc: "Can see billing and invoices" },
];

// viewReports / viewActivity / editProfile default ON, so undefined reads as on.
const DEFAULT_ON = new Set(["viewReports", "viewActivity", "editProfile"]);

export function permissionOn(permissions, key) {
  const v = permissions?.[key];
  return DEFAULT_ON.has(key) ? v !== false : v === true;
}

// Permissions that apply to this org. CRM is whitelisted per org, so counting it
// on a non-CRM org would report a denominator the member can never reach.
export function permissionFields(showCrm) {
  return PERMISSION_FIELDS.filter((f) => f.key !== "crmAccess" || showCrm);
}

// Admins implicitly hold everything; only org_user permissions are meaningful.
export function permissionCount(user, showCrm = true) {
  const fields = permissionFields(showCrm);
  if (user?.role === "org_admin" || user?.role === "main_admin") return fields.length;
  return fields.filter((f) => permissionOn(user?.permissions, f.key)).length;
}
